import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, Platform, Animated, Easing, TextInput, ScrollView, Modal, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import { ScreenBackground } from '../components/ThemeGradients';
import GradientButton from '../components/GradientButton';
import Dropdown from '../components/Dropdown';
import AutocompleteDropdown from '../components/AutocompleteDropdown';
import SearchSuggestionsDropdown from '../components/SearchSuggestionsDropdown';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useAuth } from '../context/AuthContext';
import { theme } from '../constants/theme';
import Constants from 'expo-constants';
import { queryWithCacheFallback, queryCachedSGPs } from '../services/cacheService';
import AdminScoreWidget from '../components/AdminScoreWidget';
import QueryResolutionDisplay from '../components/QueryResolutionDisplay';
import NoResultsFeedback from '../components/NoResultsFeedback';
import { sortResults, classifyMarketSection } from '../../utils/resultSorting';
import { countUniqueCards } from '../services/searchPipeline';
import _sharedSportsbooks from '../../utils/sportsbooks.json';
import { styles } from './unifiedSearchScreenStyles';
import UnifiedSearchScreenView from '../components/UnifiedSearchScreenView';
import * as betMath from '../utils/betMath';

// Hoverable wrapper for results table rows — manages its own hover state
const HoverableTableRow = ({ style, hoveredStyle, children }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <View
            style={[style, hovered && hoveredStyle]}
            {...(Platform.OS === 'web' ? {
                onMouseEnter: () => setHovered(true),
                onMouseLeave: () => setHovered(false),
            } : {})}
        >
            {children}
        </View>
    );
};

// Comprehensive search interface for both SGP and Straight betting
const UnifiedSearchScreen = () => {
    const { goBack, navigateTo, isAdmin } = useNavigation();
    const { userProfile } = useAuth();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    
    // Helper: Convert SGP cache results to display format
    const convertSGPCacheToResults = (sgpCacheData) => {
        if (!sgpCacheData?.sgps || sgpCacheData.sgps.length === 0) return [];
        
        // Group by fixture
        const fixtureMap = {};
        for (const sgp of sgpCacheData.sgps) {
            const fixtureId = sgp.fixture_id;
            if (!fixtureMap[fixtureId]) {
                fixtureMap[fixtureId] = {
                    id: fixtureId,
                    homeTeam: sgp.home_team,
                    awayTeam: sgp.away_team,
                    startDate: sgp.start_date,
                    league: sgp.league,
                    sport: sgp.sport,
                    sgps: []
                };
            }
            fixtureMap[fixtureId].sgps.push(sgp);
        }
        
        // Convert to results array
        return Object.values(fixtureMap).map(fixture => ({
            type: 'sgp',
            fixture: fixture,
            sgps: fixture.sgps,
            _fromSGPCache: true
        }));
    };
    
    // Get active sportsbook filters from user preferences
    const activeFilters = userProfile?.preferences?.selectedSportsbooks || [];
    const unitSize = userProfile?.preferences?.unitSize || 10;
    const minBooksStraights = userProfile?.preferences?.minBooksStraights || 5;
    const minBooksSGP = userProfile?.preferences?.minBooksSGP || 5;
    // ⭐ My Books - preferred sportsbooks for priority sorting
    const myBooks = userProfile?.preferences?.myBooks || [];
    const defaultSortMethod = userProfile?.preferences?.defaultSortMethod || 'ev';
    // v1.20.6 — `myBooksPriority` is derived from `defaultBookSort === 'mybooks'`
    // (the standalone preference was removed; Books Panel Default Sort is the single switch).
    const myBooksPriority = (userProfile?.preferences?.defaultBookSort === 'mybooks');
    
    // Core search state
    const [betType, setBetType] = useState('straight'); // 'straight', 'sgp', 'sgpx', 'teaser', 'round_robin'
    const [useSGPCache, setUseSGPCache] = useState(true); // Whether to check SGP cache first
    const [sport, setSport] = useState('all');
    const [league, setLeague] = useState('all');
    const [market, setMarket] = useState('all');
    const [book, setBook] = useState('all');
    const [team, setTeam] = useState('all');
    const [startTime, setStartTime] = useState('next_24h');
    const [oddsRange, setOddsRange] = useState({ min: '', max: '' });
    const [bookCount, setBookCount] = useState({ min: 1, max: 20 });
    const [boostPercent, setBoostPercent] = useState('0'); // Boost percentage for profit boost promos
    const [sharpBook, setSharpBook] = useState('all');
    const [future, setFuture] = useState(false);
    const [live, setLive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]); // Multi-select sportsbooks
    const [showSportsbooksDropdown, setShowSportsbooksDropdown] = useState(false); // Dropdown visibility

    // ✨ Search bar resolution animation state
    const [resolvedSearchText, setResolvedSearchText] = useState(null); // resolved query shown in the bar overlay
    const [searchInputFocused, setSearchInputFocused] = useState(false); // overlay hides when focused
    const [showOriginalHint, setShowOriginalHint] = useState(false);    // hover shows original query
    const [wipeComplete, setWipeComplete] = useState(false);            // scan line gone after anim
    const searchInputRef = useRef(null);
    const wipeAnim = useRef(new Animated.Value(0)).current;
    
    // Advanced filters
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [homeTeams, setHomeTeams] = useState([]);
    const [awayTeams, setAwayTeams] = useState([]);
    const [conference, setConference] = useState('all');
    const [division, setDivision] = useState('all');
    const [players, setPlayers] = useState([]);
    const [position, setPosition] = useState('all');
    const [experience, setExperience] = useState('all');
    const [spreadRange, setSpreadRange] = useState({ min: '', max: '' });
    const [totalRange, setTotalRange] = useState({ min: '', max: '' });
    const [evRange, setEvRange] = useState({ min: '', max: '' });
    const [kellyCriterion, setKellyCriterion] = useState('0.25');
    
    // Results state
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cacheStatus, setCacheStatus] = useState(null);
    const [injuryAlert, setInjuryAlert] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [totalSportsbooks, setTotalSportsbooks] = useState(0); // Track unique sportsbooks in results
    const [selectedBets, setSelectedBets] = useState([]); // Track selected bets for SGP
    
    // ⭐ Admin scoring state - track current query assessment
    const [currentQueryId, setCurrentQueryId] = useState(null);
    const [queryAssessment, setQueryAssessment] = useState(null);
    
    // ⭐ Query resolution display state
    const [queryResolution, setQueryResolution] = useState(null);

    // 🎯 Query classification state — used for query-type-aware rendering
    const [resultType, setResultType] = useState(null);            // e.g. 'MATCHUP_H2H', 'MATCHUP_FALLBACK', 'PLAYER_NO_DATA'
    const [fixtureInfo, setFixtureInfo] = useState(null);          // { home_team, away_team, start_date } for matchup header
    const [queryClassification, setQueryClassification] = useState(null); // full _queryClassification object

    // 🗂️ Results section filter state
    const [activeSection, setActiveSection] = useState('all');     // 'all' | 'game_lines' | 'player_props' | 'derivatives'
    const [activePlayerFilter, setActivePlayerFilter] = useState(null); // player name string or null for all
    
    // ✨ Trigger the search bar wipe animation when a new query resolves
    useEffect(() => {
        if (loading || !queryClassification) return;

        // Build the resolved text directly from classification entities
        const e = queryClassification.entities || {};
        const type = queryClassification.type || '';
        const parts = [];
        const player = e.player?.name || e.player;
        const team   = e.team?.name   || e.team;
        const market = e.market;
        const league = e.league || e.player?.league || e.team?.league;
        if (type === 'MATCHUP_QUERY' && e.matchup) {
            parts.push(`${e.matchup.team1} vs ${e.matchup.team2}`);
        } else if (player) {
            parts.push(player);
            if (e.lineDirection && e.lineThreshold != null) parts.push(`${e.lineDirection} ${e.lineThreshold}`);
            if (market) parts.push(market);
        } else if (team) {
            parts.push(team);
            if (market) parts.push(market);
        } else if (league) {
            parts.push(league.toUpperCase());
            if (market) parts.push(market);
        }
        if (e.sportsbook) parts.push(`@ ${e.sportsbook}`);

        const resolvedText = parts.join(' · ');
        if (!resolvedText || resolvedText.trim().toLowerCase() === searchQuery.trim().toLowerCase()) {
            setResolvedSearchText(null);
            return;
        }

        // Start the wipe animation
        setResolvedSearchText(resolvedText);
        setWipeComplete(false);
        setShowOriginalHint(false);
        wipeAnim.setValue(0);
        Animated.timing(wipeAnim, {
            toValue: 1,
            duration: 900,
            delay: 350,
            easing: Easing.bezier(0.25, 0.1, 0.25, 1),
            useNativeDriver: false,
        }).start(() => setWipeComplete(true));
    }, [loading, queryClassification]);

    // Smart dropdown data based on sport selection
    // Using sport-specific markets to avoid showing irrelevant options (e.g., no rushing for basketball)
    const sportData = {
        'all': { 
            leagues: ['NBA', 'NFL', 'MLB', 'NHL', 'NCAAF', 'NCAAB', 'MLS', 'Premier League'], 
            markets: ['Moneyline', 'Spread', 'Total', 'Player Props'], 
            positions: [] 
        },
        'basketball': {
            leagues: ['NBA', 'WNBA', 'NCAAB', 'G League', 'EuroLeague'],
            markets: [
                // Game markets
                'Moneyline', 'Point Spread', 'Total Points', '1st Half Moneyline', '1st Half Spread', 
                '1st Half Total', '1st Quarter Moneyline', '1st Quarter Spread', '1st Quarter Total', 
                'Team Total', 'Alternate Spread', 'Alternate Total',
                // Player markets (basketball-specific)
                'Player Points', 'Player Rebounds', 'Player Assists', 'Player Points + Rebounds', 
                'Player Points + Assists', 'Player Rebounds + Assists', 'Player Points + Rebounds + Assists', 
                'Player Steals', 'Player Blocks', 'Player Made Threes', 'Player Double Double', 
                'Player Triple Double', 'Player Turnovers'
            ],
            positions: ['PG', 'SG', 'SF', 'PF', 'C']
        },
        'football': {
            leagues: ['NFL', 'NCAAF', 'CFL', 'XFL'],
            markets: [
                // Game markets
                'Moneyline', 'Point Spread', 'Total Points', '1st Half Moneyline', '1st Half Spread',
                '1st Half Total', '1st Quarter Spread', '1st Quarter Total', 'Team Total', 
                'Alternate Spread', 'Alternate Total',
                // Player markets (football-specific)
                'Player Passing Yards', 'Player Passing TDs', 'Player Rushing Yards', 'Player Rushing TDs', 
                'Player Receiving Yards', 'Player Receptions', 'Anytime Touchdown', 'First Touchdown',
                'Player Passing Attempts', 'Player Completions', 'Player Interceptions'
            ],
            positions: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF']
        },
        'baseball': {
            leagues: ['MLB'],
            markets: [
                // Game markets
                'Moneyline', 'Run Line', 'Total Runs', '1st 5 Innings Moneyline', '1st 5 Innings Run Line', 
                '1st 5 Innings Total', '1st Inning Result', 'Team Total', 'Alternate Run Line', 'Alternate Total',
                // Player markets (baseball-specific)
                'Player Hits', 'Player Home Runs', 'Player RBIs', 'Player Total Bases', 'Player Runs Scored',
                'Pitcher Strikeouts', 'Player Stolen Bases', 'Player Walks'
            ],
            positions: ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH']
        },
        'hockey': {
            leagues: ['NHL'],
            markets: [
                // Game markets
                'Moneyline', 'Puck Line', 'Total Goals', '1st Period Moneyline', '1st Period Puck Line', 
                '1st Period Total', 'Team Total', 'Regulation Time Result', '60 Minute Line',
                // Player markets (hockey-specific)
                'Player Goals', 'Player Assists', 'Player Points', 'Player Shots on Goal', 'Player Saves',
                'Anytime Goalscorer', 'First Goalscorer', 'Player Blocked Shots'
            ],
            positions: ['C', 'LW', 'RW', 'D', 'G']
        },
        'soccer': {
            leagues: ['MLS', 'Premier League', 'La Liga', 'Serie A', 'Bundesliga', 'Ligue 1', 'Champions League'],
            markets: [
                // Game markets
                'Moneyline', 'Draw No Bet', 'Double Chance', 'Total Goals', 'Both Teams to Score', 
                'Asian Handicap', 'Correct Score', '1st Half Result', '1st Half Total',
                // Player markets (soccer-specific)
                'Player Goals', 'Player Assists', 'Player Shots', 'Anytime Goalscorer', 'First Goalscorer'
            ],
            positions: ['GK', 'DEF', 'MID', 'FWD']
        },
        'tennis': {
            leagues: ['ATP', 'WTA', 'Grand Slams', 'Masters 1000', 'Challenger'],
            markets: [
                // Game markets
                'Moneyline', 'Set Spread', 'Total Sets', 'Total Games', '1st Set Winner', '1st Set Total Games',
                // Player markets (tennis-specific)
                'Player Aces', 'Player Double Faults'
            ],
            positions: []
        },
        'golf': {
            leagues: ['PGA Tour', 'European Tour', 'LPGA', 'Champions Tour', 'Majors'],
            markets: [
                'Tournament Winner', 'Top 5', 'Top 10', 'Top 20', 'Make Cut', 'First Round Leader', 'Head to Head'
            ],
            positions: []
        }
    };
    
    // Get filtered options based on sport
    const getFilteredOptions = (sportKey) => {
        const sportInfo = sportData[sportKey] || sportData['all'];
        return {
            leagues: sportInfo.leagues,
            markets: sportInfo.markets,
            positions: sportInfo.positions
        };
    };
    
    const _CATEGORY_TO_TIER = { major: 1, secondary: 2, international: 3, dfs: 4, exchange: 3, canada: 3, australia: 3, other: 4 };
    const ALL_SPORTSBOOKS = _sharedSportsbooks.map(b => ({
        name: b.name,
        tier: _CATEGORY_TO_TIER[b.category] || 4,
        popular: b.category === 'major',
    }));
    
    // Boost percentage options (5% increments)
    const BOOST_OPTIONS = [
        { value: '0', label: 'No Boost' },
        { value: '5', label: '5%' },
        { value: '10', label: '10%' },
        { value: '15', label: '15%' },
        { value: '20', label: '20%' },
        { value: '25', label: '25%' },
        { value: '30', label: '30%' },
        { value: '35', label: '35%' },
        { value: '40', label: '40%' },
        { value: '45', label: '45%' },
        { value: '50', label: '50%' },
        { value: '75', label: '75%' },
        { value: '100', label: '100%' },
    ];
    
    // Toggle sportsbook selection
    const toggleSportsbook = (bookName) => {
        setSelectedSportsbooks(prev => {
            if (prev.includes(bookName)) {
                return prev.filter(b => b !== bookName);
            } else {
                return [...prev, bookName];
            }
        });
    };
    
    // Select all popular sportsbooks
    const selectPopularSportsbooks = () => {
        const popular = ALL_SPORTSBOOKS.filter(b => b.popular).map(b => b.name);
        setSelectedSportsbooks(popular);
    };
    
    // Clear all sportsbook selections
    const clearSportsbookSelections = () => {
        setSelectedSportsbooks([]);
    };
    
    // Time range options
    const timeRanges = [
        { value: 'next_24h', label: 'Next 24 Hours' },
        { value: 'next_48h', label: 'Next 48 Hours' },
        { value: 'next_week', label: 'Next Week' },
        { value: 'today', label: 'Today Only' },
        { value: 'tomorrow', label: 'Tomorrow Only' },
        { value: 'this_weekend', label: 'This Weekend' },
        { value: 'custom', label: 'Custom Range' }
    ];
    
    // Odds range presets
    const oddsPresets = [
        { value: 'favorites', label: 'Favorites (-200 to -110)' },
        { value: 'even', label: 'Even Money (-110 to +110)' },
        { value: 'underdogs', label: 'Underdogs (+110 to +500)' },
        { value: 'longshots', label: 'Longshots (+500+)' },
        { value: 'custom', label: 'Custom Range' }
    ];
    
    // Search execution
    const executeSearch = async () => {
        setLoading(true);
        setCurrentPage(1);
        setCacheStatus(null);
        setQueryResolution(null);
        setResultType(null);
        setFixtureInfo(null);
        setQueryClassification(null);
        setResolvedSearchText(null);
        setActiveSection('all');
        setActivePlayerFilter(null);
        
        const searchStartTime = Date.now();
        const searchLogs = [];
        
        // Build search parameters
        const searchParams = {
            betType,
            sport: sport !== 'all' ? sport : null,
            league: league !== 'all' ? league : null,
            market: market !== 'all' ? market : null,
            book: book !== 'all' ? book : null,
            team: team !== 'all' ? team : null,
            startTime,
            oddsRange,
            bookCount,
            boostPercent,
            sharpBook: sharpBook !== 'all' ? sharpBook : null,
            future,
            live,
            query: searchQuery,
            // Advanced filters
            homeTeams: homeTeams.length > 0 ? homeTeams : null,
            awayTeams: awayTeams.length > 0 ? awayTeams : null,
            conference: conference !== 'all' ? conference : null,
            division: division !== 'all' ? division : null,
            players: players.length > 0 ? players : null,
            position: position !== 'all' ? position : null,
            experience: experience !== 'all' ? experience : null,
            spreadRange,
            totalRange,
            evRange,
            kellyCriterion
        };
        
        searchLogs.push({ timestamp: new Date().toISOString(), event: 'search_started', params: searchParams });
        
        try {
            // Build search prompt - ONLY include the user's actual search text
            // Structured filters (sport, league, market, time) go as parameters, NOT in the prompt
            // This prevents confusing the backend with redundant/contradictory prompt text
            const promptParts = [];
            
            // ONLY add league to prompt if no search query text (need something to classify)
            if (searchQuery.trim()) {
                promptParts.push(searchQuery.trim());
            } else {
                // No search text - build a descriptive query from filters
                if (league !== 'all') {
                    promptParts.push(league.toUpperCase());
                } else if (sport !== 'all') {
                    promptParts.push(sport);
                }
                if (market !== 'all') {
                    promptParts.push(market.replace(/_/g, ' '));
                }
            }
            
            // Add time filter as a hint word (backend understands these)
            if (startTime && startTime !== 'all') {
                if (startTime === 'next_24h') promptParts.push('today');
                else if (startTime === 'next_48h') promptParts.push('next 2 days');
                else if (startTime === 'next_week') promptParts.push('this week');
            }
            
            // Add future/live filters
            if (future) promptParts.push('futures');
            if (live) promptParts.push('live');
            
            const prompt = promptParts.length > 0 ? promptParts.join(' ') : 'best bets today';
            
            console.log('[UnifiedSearch] Starting cache-first search with prompt:', prompt);
            
            // Use selected sportsbooks from multi-select dropdown if any, otherwise fall back to single book selection
            const effectiveSportsbooks = selectedSportsbooks.length > 0 
                ? selectedSportsbooks.join(',') 
                : (book !== 'all' ? book : undefined);
            
            // ⚡ CHECK SGP CACHE FIRST if bet type is SGP
            // ⭐ UNIFIED with runSearchPipeline's shortcut rules (one pipeline, one answer):
            //    - same pool size (500) and freshness window (24h) as SgpFlow/Background
            //    - skip the shortcut when the user scoped to specific sportsbooks, so
            //      nlOdds can apply its universal sportsbook filter instead
            let sgpCacheResult = null;
            if ((betType === 'sgp' || betType === 'sgpx') && useSGPCache && !effectiveSportsbooks) {
                console.log('[UnifiedSearch] 🎯 Checking SGP cache first...');
                setCacheStatus({ stage: 'checking_sgp_cache', message: 'Checking pre-generated SGPs...' });
                
                try {
                    const { queryCachedSGPs } = await import('../services/cacheService');
                    sgpCacheResult = await queryCachedSGPs({
                        league: league !== 'all' ? league : undefined,
                        sport: sport !== 'all' ? sport : undefined,
                        team: team !== 'all' ? team : undefined,
                        limit: 500,
                        max_age_hours: 24,
                        sort_by: 'ev'
                    });
                    
                    console.log('[UnifiedSearch] SGP cache result:', {
                        success: sgpCacheResult?.success,
                        sgpsCount: sgpCacheResult?.sgps?.length || 0,
                        fixturesCount: sgpCacheResult?.fixtures?.length || 0
                    });
                    
                    if (sgpCacheResult?.success && sgpCacheResult?.sgps?.length > 0) {
                        console.log('[UnifiedSearch] ✅ Using SGPs from cache!');
                        setCacheStatus({ stage: 'sgp_cache_hit', message: `Loaded ${sgpCacheResult.sgps.length} pre-generated SGPs` });
                        
                        // Convert SGP cache to results format
                        const resultsFromCache = convertSGPCacheToResults(sgpCacheResult);
                        setResults(resultsFromCache);
                        setTotalResults(sgpCacheResult.sgps.length);
                        setTotalPages(Math.ceil(sgpCacheResult.sgps.length / 20));
                        setLoading(false);
                        return; // Exit early - we have our results!
                    } else {
                        console.log('[UnifiedSearch] ℹ️ No SGPs in cache, falling back to live generation');
                        setCacheStatus({ stage: 'sgp_cache_miss', message: 'No cached SGPs - generating live...' });
                    }
                } catch (sgpCacheError) {
                    console.warn('[UnifiedSearch] Error checking SGP cache:', sgpCacheError);
                    setCacheStatus({ stage: 'sgp_cache_error', message: 'SGP cache error - using live generation' });
                }
            }
            
            // ⚡ Use cache-first approach
            const queryResult = await queryWithCacheFallback(
                prompt,
                {
                    league: league !== 'all' ? league : undefined,
                    sport: sport !== 'all' ? sport : undefined,
                    team: team !== 'all' ? team : undefined,
                    market: market !== 'all' ? market : undefined,
                prefer: betType === 'straight' ? 'straight' : 'sgp',
                // ⭐ User tracking for query analytics
                userId: userProfile?.uid,
                userEmail: userProfile?.email,
                userName: userProfile?.username || userProfile?.displayName,
                defaults: {
                    sport: sport !== 'all' ? sport : undefined,
                    league: league !== 'all' ? league?.toLowerCase() : undefined,
                    // ⭐⭐⭐ Do NOT send sportsbooks as query constraints — backend returns all books.
                    // myBooks + selectedSportsbooks are only used to skip regional→parent merging in dedupe
                    // when the user explicitly picked regional skins in profile or session filters.
                    myBooks: myBooks || [],
                    selectedSportsbooks: [...new Set([
                        ...(userProfile?.preferences?.selectedSportsbooks || []),
                        ...selectedSportsbooks,
                    ])],
                    preferredLeagues: userProfile?.preferences?.tickerLeagues || [],
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    now: new Date().toISOString()
                }
                },
                (status) => {
                    // Update cache status for user feedback
                    setCacheStatus(status);
                    console.log('[UnifiedSearch] Cache status:', status);
                }
            );
            
            searchLogs.push({ 
                timestamp: new Date().toISOString(), 
                event: 'query_complete',
                cached: queryResult.cached 
            });
            
            if (!queryResult.success) {
                console.error('[UnifiedSearch] Query failed:', queryResult._message || queryResult.error);
                setResults([]);
                setTotalResults(0);
                setTotalSportsbooks(0);
                setTotalPages(0);
                setCacheStatus({ stage: 'error', message: queryResult._message || 'Search failed' });
                return;
            }
            
            // ⭐ Check for backend processing errors (returned as 200 with _error field)
            if (queryResult._error || queryResult._errorType) {
                console.warn('[UnifiedSearch] Backend returned error:', queryResult._error, queryResult._message);
                setCacheStatus({ stage: 'error', message: queryResult._message || queryResult._error || 'Processing error' });
            }
            
            // ⭐ Check for no-data responses (PLAYER_NO_DATA, BEST_BETS_NO_DATA, early exits, etc.)
            if (queryResult._message && ((queryResult._resultType || '').includes('NO_DATA') || queryResult._totalResults === 0)) {
                console.log('[UnifiedSearch] No data response:', queryResult._resultType, queryResult._message);
                setCacheStatus({ stage: 'info', message: queryResult._message });
            }

            // ⭐ N2: clarify-or-abstain — this screen has no chips UI, so surface the
            // question + candidates as an info banner (SgpFlow renders tappable chips).
            if (queryResult.needsClarification && queryResult.clarification) {
                const _opts = (queryResult.clarification.options || []).map(o => o.label || o.team).join(' / ');
                setCacheStatus({ stage: 'info', message: `${queryResult.clarification.question}${_opts ? ` (${_opts})` : ''} Add the city or league to your search.` });
            }

            // ⭐ N3: broadened/fallback results are always disclosed, never silent.
            if (queryResult._broadened?.note) {
                setCacheStatus({ stage: 'info', message: queryResult._broadened.note });
            }

            // 🏥 Injury alert from backend
            if (queryResult._injuryAlert) {
                setInjuryAlert(queryResult._injuryAlert);
            } else {
                setInjuryAlert(null);
            }

            // 🎯 Extract user's requested line from backend classification
            const qcEntities = queryResult._queryClassification?.entities || {};
            const userLineThreshold = qcEntities.lineThreshold ?? null;
            const userLineDirection = qcEntities.lineDirection ?? null;
            const userSpreadLine = qcEntities.spreadLine ?? null;

            // 🎯 Store result type, fixture info, and full classification for rendering
            setResultType(queryResult._resultType || null);
            setQueryClassification(queryResult._queryClassification || null);
            const firstResultGroup = Array.isArray(queryResult.results) ? queryResult.results[0] : null;
            setFixtureInfo(firstResultGroup?.fixture || null);
            
            // Parse results (format depends on cache vs live)
            let bodyJson = queryResult;
            
            // If we got odds directly from cache, wrap them in the expected format
            if (queryResult.odds) {
                bodyJson = {
                    results: [{
                        items: queryResult.odds
                    }]
                };
            }
            
            const arr = Array.isArray(bodyJson?.results) ? bodyJson.results : [];
            const items = arr.flatMap(r => Array.isArray(r?.items) ? r.items : [])
                .flatMap(x => Array.isArray(x?.odds) ? x.odds : [x]);
            
            searchLogs.push({ 
                timestamp: new Date().toISOString(), 
                event: 'results_parsed', 
                resultsCount: arr.length, 
                itemsCount: items.length,
                endpoints: bodyJson?.endpoints || []
            });
            
            // Transform backend results to table format
            // ⭐⭐⭐ FIX: Don't slice here - slice AFTER all frontend filters are applied
            // Previously .slice(0, 100) was applied before filtering, which meant
            // filters could remove most results leaving far fewer than 100 visible.
            const transformedResults = items
                .filter(o => o && (o.market || o.market_id || o.market_key) && (o.selection || o.name || o.selection_normalized))
                .map((o, idx) => {
                    const price = o.price ?? o.american_price ?? null;
                    const aiProb = o.ai_prob ?? null;
                    let implied = null;
                    let ev = null;
                    
                    if (typeof price === 'number' && !isNaN(price)) {
                        implied = betMath.americanToImplied(price);
                    }
                    
                    // Canonical EV matching the card badge: min(additive, mult, power).
                    // Same conservative logic as UnifiedBetCard's ev useMemo + getCanonicalSortEv.
                    // This ensures sort order always matches the EV% shown on each card.
                    const _m1 = o.ev_method1_additive ?? null;
                    const _m2 = o.ev_method2_multiplicative ?? null;
                    const _m3 = o.ev_method3_power ?? null;
                    const _isSGPev = o.ev_method === 'individual_leg_devig';
                    // Sort key = min across all available methods (same as badge — no outlier gate).
                    // SGPs exclude additive since per-leg devig makes it unreliable for parlays.
                    const _methodsForSort = [
                        ...(!_isSGPev && _m1 != null ? [_m1] : []),
                        ...(_m2 != null ? [_m2] : []),
                        ...(_m3 != null ? [_m3] : []),
                    ];
                    if (_methodsForSort.length > 0) {
                        ev = Math.min(..._methodsForSort);
                    } else if (o.ev_percent !== null && o.ev_percent !== undefined) {
                        ev = o.ev_percent;
                    } else if (aiProb !== null && implied !== null) {
                        const decimalOdds = betMath.americanToDecimal(price);
                        ev = decimalOdds != null ? ((aiProb * decimalOdds) - 1) * 100 : null;
                    }
                    
                    // Calculate fair value (convert AI prob to American odds)
                    let fairValue = null;
                    if (aiProb !== null && aiProb > 0 && aiProb < 1) {
                        fairValue = betMath.impliedToAmerican(aiProb);
                    }
                    
                    // Calculate Kelly
                    const kelly = (ev !== null && ev > 0 && aiProb !== null && implied !== null)
                        ? (betMath.kellyFraction(aiProb, price, 1) ?? 0) * 100
                        : 0;
                    
                    // ⭐ FIX: Get sportsbook count from bookOdds (the correct source)
                    // For team markets: bookOdds has ALL sportsbooks for this exact bet line
                    // For player props: bookOdds has books for THIS specific line, but we also
                    // track total unique sportsbooks across all lines in sportsbook_count
                    const bookOdds = o.bookOdds || {};
                    const bookOddsCount = Object.keys(bookOdds).length;
                    
                    // ⭐ SMART COUNT: Use bookOdds if it has significant data (team markets),
                    // otherwise use sportsbook_count (total unique books for player props)
                    // The threshold of 5 catches cases where bookOdds is sparse due to many alt lines
                    const bookCount = bookOddsCount >= 5 
                        ? bookOddsCount  // Team markets: 100+ books in one bookOdds
                        : Math.max(bookOddsCount, o.sportsbook_count || o.book_count || 1); // Player props: use total count
                    
                    const allBooks = Object.keys(bookOdds);
                    
                    // ⭐ FIX: Find best odds across all sportsbooks for display
                    let bestPrice = price;
                    let bestBook = o.sportsbook || 'N/A';
                    if (allBooks.length > 0) {
                        // Find the best price (highest for positive odds, closest to 0 for negative)
                        allBooks.forEach(book => {
                            const bookData = bookOdds[book];
                            const bookPrice = typeof bookData === 'object' ? bookData.price : bookData;
                            if (bookPrice != null) {
                                // For favorites (negative odds): less negative is better (-110 > -120)
                                // For underdogs (positive odds): higher is better (+150 > +130)
                                if (bestPrice == null || 
                                    (bookPrice > 0 && bestPrice > 0 && bookPrice > bestPrice) ||
                                    (bookPrice < 0 && bestPrice < 0 && bookPrice > bestPrice) ||
                                    (bookPrice > 0 && bestPrice < 0)) {
                                    bestPrice = bookPrice;
                                    bestBook = book;
                                }
                            }
                        });
                    }
                    
                    // ⭐ For player props, 'name' has full line (e.g., "Jaylen Brown Over 24.5")
                    // while 'selection' just has player name. Prefer 'name' for display.
                    const displayBet = o.name || o.selection || 'N/A';
                    // Don't add selection_line if name already contains "Over" or "Under"
                    const hasLine = /\b(over|under)\b/i.test(displayBet);
                    const lineSuffix = !hasLine && (o.points || o.selection_line) ? ` ${o.points || o.selection_line}` : '';

                    // 🎯 Compute distance from user's requested line
                    let lineDistance = null;
                    let isRequestedLine = false;
                    let directionMatch = true;
                    const effectiveThreshold = userSpreadLine != null ? userSpreadLine : userLineThreshold;
                    if (effectiveThreshold != null) {
                        const itemLine = parseFloat(o.selection_line ?? o.points ?? o.line ?? o.handicap ?? NaN);
                        if (!isNaN(itemLine)) {
                            lineDistance = Math.abs(itemLine - effectiveThreshold);
                            isRequestedLine = lineDistance < 0.25;
                        }
                        if (userLineDirection) {
                            const sel = (o.selection || o.name || '').toLowerCase();
                            const hasOver = /\bover\b/i.test(sel);
                            const hasUnder = /\bunder\b/i.test(sel);
                            if (userLineDirection === 'over' && hasUnder) directionMatch = false;
                            if (userLineDirection === 'under' && hasOver) directionMatch = false;
                        }
                    }
                    
                    const _resultSection = o._resultSection || classifyMarketSection(o.market, o._isPlayerProp);

                    return {
                        id: o.id || `${idx}`,
                        league: (o.league || o.sport || '').toUpperCase(),
                        date: o.commence_time ? new Date(o.commence_time).toLocaleDateString() : 'TBD',
                        time: o.commence_time ? new Date(o.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'TBD',
                        game: o.fixture || o.game || `${o.home_team || ''} vs ${o.away_team || ''}` || 'N/A',
                        market: o.market || o.market_id || 'N/A',
                        bet: `${displayBet}${lineSuffix}`,
                        book: bestBook,
                        odds: bestPrice !== null ? (bestPrice > 0 ? `+${bestPrice}` : `${bestPrice}`) : 'N/A',
                        booksCalculated: bookCount,
                        allBooks: allBooks,
                        bookOdds: bookOdds,
                        fairValue: fairValue !== null ? (fairValue > 0 ? `+${fairValue}` : `${fairValue}`) : 'N/A',
                        evPercent: ev !== null ? ev.toFixed(2) : '0.00',
                        kelly: Math.max(0, Math.min(25, kelly)).toFixed(1),
                        is_main: o.is_main,
                        isMain: o.is_main === true,
                        injuryStatus: o._injury_status || null,
                        isInjured: o._is_injured || false,
                        injuryDetails: o._injury_details || null,
                        lineDistance,
                        isRequestedLine,
                        directionMatch,
                        _resultSection,
                        _isPlayerProp: o._isPlayerProp || false,
                        raw: o
                    };
                });
            
            // Apply additional filters
            let filteredResults = transformedResults;
            
            // ⭐⭐⭐ FIX: Book filters now check ALL available books via bookOdds/allBooks,
            // not just r.book (which is the single best-priced book).
            // A result available on 10 books should show if ANY of those match the filter.
            
            // Filter by book if specified (from dropdown)
            if (book !== 'all') {
                const bookNorm = book.toLowerCase().replace(/[^a-z0-9]/g, '');
                filteredResults = filteredResults.filter(r => {
                    const booksToCheck = r.allBooks && r.allBooks.length > 0 
                        ? r.allBooks 
                        : [r.book];
                    return booksToCheck.some(b => {
                        const bNorm = (b || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                        return bNorm === bookNorm;
                    });
                });
            }
            
            // Filter by user's preferred sportsbooks (from settings > Sportsbook Filters)
            // Check ALL available books, not just the best-priced one
            if (activeFilters.length > 0) {
                const normalizedActiveFilters = new Set(activeFilters.map(f => f.toLowerCase().replace(/[^a-z0-9]/g, '')));
                filteredResults = filteredResults.filter(r => {
                    const booksToCheck = r.allBooks && r.allBooks.length > 0 
                        ? r.allBooks 
                        : [r.book];
                    return booksToCheck.some(bookName => {
                        const bookNorm = (bookName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                        return normalizedActiveFilters.has(bookNorm);
                    });
                });
            }
            
            // Filter by multi-select sportsbooks dropdown (session-level filter)
            if (selectedSportsbooks.length > 0) {
                const normalizedSelections = new Set(selectedSportsbooks.map(s => s.toLowerCase().replace(/[^a-z0-9]/g, '')));
                filteredResults = filteredResults.filter(r => {
                    const booksToCheck = r.allBooks && r.allBooks.length > 0
                        ? r.allBooks
                        : [r.book];
                    return booksToCheck.some(b => {
                        const bNorm = (b || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                        return normalizedSelections.has(bNorm);
                    });
                });
            }

            // Filter by min-books preference (ensures enough sportsbooks confirm the line)
            const effectiveMinBooks = betType === 'sgp' ? minBooksSGP : minBooksStraights;
            if (effectiveMinBooks > 1) {
                filteredResults = filteredResults.filter(r => (r.booksCalculated || 1) >= effectiveMinBooks);
            }

            // Filter by EV range
            if (evRange.min !== '' || evRange.max !== '') {
                filteredResults = filteredResults.filter(r => {
                    const ev = parseFloat(r.evPercent);
                    if (evRange.min !== '' && ev < parseFloat(evRange.min)) return false;
                    if (evRange.max !== '' && ev > parseFloat(evRange.max)) return false;
                    return true;
                });
            }
            
            // Apply Kelly Criterion to calculate suggested bet size
            const kellyFraction = parseFloat(kellyCriterion) || 0.25;
            filteredResults = filteredResults.map(r => {
                const ev = parseFloat(r.evPercent);
                const aiProb = parseFloat(r.kelly) / 100; // Convert kelly% back to probability
                return { ...r, kellyCriterion: kellyFraction, suggestedKelly: parseFloat(r.kelly) };
            });
            
            // 🎯 Query-type-aware sorting — always run client sort so EV order matches card badges.
            // Never skip based on _backendSorted: the backend may return section-grouped order
            // (game_lines → derivatives → props) which does NOT match badge EV% descending.
            // v1.20.8 — when exactly one book is the focus (either typed in
            // the query or selected as the sole post-result filter), pass it
            // through as `preferredSportsbookKey` so the sort axis matches
            // the focused-book EV the cards display.
            const _focusBookKey =
                (selectedSportsbooks.length === 1 ? selectedSportsbooks[0] : null)
                || queryResult?._preferredSportsbook
                || queryResult?._queryClassification?.sportsbook
                || null;
            filteredResults = sortResults(filteredResults, queryResult._queryClassification, {
                myBooks,
                defaultSortMethod,
                myBooksPriority: myBooksPriority && myBooks.length > 0,
                preferredSportsbookKey: _focusBookKey,
            });
            
            // ⭐⭐⭐ FIX: Apply result limit AFTER all filters, not before
            // This ensures users see up to 200 results after filtering instead of
            // getting 100 pre-filter then losing most to sportsbook/EV filters
            const totalBeforeLimit = filteredResults.length;
            filteredResults = filteredResults.slice(0, 200);
            
            searchLogs.push({
                timestamp: new Date().toISOString(),
                event: 'search_completed',
                finalResultsCount: filteredResults.length,
                totalBeforeLimit
            });

            // ⭐ Calculate total unique sportsbooks across all results
            const allSportsbooks = new Set();
            filteredResults.forEach(r => {
                if (r.allBooks && r.allBooks.length > 0) {
                    r.allBooks.forEach(book => allSportsbooks.add(book));
                }
                if (r.book) allSportsbooks.add(r.book);
            });
            
            // Also check response-level sportsbook_count for player props
            const responseSportsbookCount = queryResult.sportsbook_count || 0;
            const totalBooks = Math.max(allSportsbooks.size, responseSportsbookCount);

            setResults(filteredResults);
            // Show unique card count (one per player/market, not one per alt-line or book).
            setTotalResults(countUniqueCards(filteredResults));
            setTotalSportsbooks(totalBooks);
            setTotalPages(Math.ceil(filteredResults.length / 10));
            
            // ⭐ Generate queryId for admin scoring (matches backend format)
            const queryId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            setCurrentQueryId(queryId);
            
            // ⭐ Store query resolution for display
            if (queryResult._queryResolution) {
                setQueryResolution(queryResult._queryResolution);
            }
            
            // ⭐ Calculate basic assessment info for display (real scores come from backend)
            const searchDuration = Date.now() - searchStartTime;
            setQueryAssessment({
              autoScore: null, // Real score from backend
              autoGrade: null,
              timing: searchDuration,
              resultCount: filteredResults.length,
              cacheHit: queryResult.cached || cacheStatus?.stage === 'cache_hit',
              query: prompt || searchQuery
            });
            
            // Save search to metrics
            await saveSearchMetrics(prompt, searchParams, filteredResults.length, searchLogs, searchDuration);
            
        } catch (error) {
            console.error('Search error:', error);
            searchLogs.push({ timestamp: new Date().toISOString(), event: 'search_exception', error: error.message });
            setResults([]);
            setTotalResults(0);
            setTotalSportsbooks(0);
            setTotalPages(0);
            
            // Save failed search to metrics
            await saveSearchMetrics(searchQuery, searchParams, 0, searchLogs, Date.now() - searchStartTime);
        } finally {
            setLoading(false);
        }
    };
    
    // Save search metrics to Firestore
    const saveSearchMetrics = async (query, params, resultsCount, logs, duration) => {
        try {
            const { db, auth } = require('../firebaseConfig');
            const { collection, addDoc, serverTimestamp } = require('firebase/firestore');
            
            await addDoc(collection(db, 'metrics_queries'), {
                prompt: query,
                params: params,
                resultsCount: resultsCount,
                logs: logs,
                durationMs: duration,
                uid: auth.currentUser?.uid || 'anonymous',
                createdAt: serverTimestamp(),
                broaden: false,
                feedback: null
            });
        } catch (error) {
            console.error('Failed to save search metrics:', error);
        }
    };
    
    // Generate mock results for testing
    const generateMockResults = (params) => {
        const mockData = [];
        const leagues = params.league ? [params.league] : ['NBA', 'NFL', 'MLB'];
        const books = params.book ? [params.book] : ['DraftKings', 'FanDuel', 'Caesars'];
        
        for (let i = 0; i < 25; i++) {
            const league = leagues[Math.floor(Math.random() * leagues.length)];
            const book = books[Math.floor(Math.random() * books.length)];
            const ev = (Math.random() - 0.4) * 10; // -4% to +6% EV
            
            mockData.push({
                id: i + 1,
                league,
                date: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                time: `${Math.floor(Math.random() * 24)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
                game: `${getRandomTeam()} vs ${getRandomTeam()}`,
                market: getRandomMarket(league),
                bet: getRandomBet(league),
                book,
                odds: Math.random() > 0.5 ? `+${Math.floor(Math.random() * 500) + 100}` : `-${Math.floor(Math.random() * 200) + 110}`,
                booksCalculated: Math.floor(Math.random() * 8) + 3,
                fairValue: Math.random() > 0.5 ? `+${Math.floor(Math.random() * 500) + 100}` : `-${Math.floor(Math.random() * 200) + 110}`,
                evPercent: ev.toFixed(2),
                kelly: Math.max(0, Math.min(25, ev * 2)).toFixed(1)
            });
        }
        
        return mockData.sort((a, b) => parseFloat(b.evPercent) - parseFloat(a.evPercent));
    };
    
    const getRandomTeam = () => {
        const teams = ['Lakers', 'Warriors', 'Celtics', 'Heat', 'Nuggets', 'Bucks', 'Suns', 'Mavericks'];
        return teams[Math.floor(Math.random() * teams.length)];
    };
    
    const getRandomMarket = (league) => {
        const markets = {
            'NBA': ['Moneyline', 'Spread', 'Total', 'Player Points', 'Player Rebounds'],
            'NFL': ['Moneyline', 'Spread', 'Total', 'Player Passing Yards', 'Player Touchdowns'],
            'MLB': ['Moneyline', 'Run Line', 'Total', 'Player Hits', 'Player RBIs']
        };
        const leagueMarkets = markets[league] || ['Moneyline', 'Spread', 'Total'];
        return leagueMarkets[Math.floor(Math.random() * leagueMarkets.length)];
    };
    
    const getRandomBet = (league) => {
        const bets = {
            'NBA': ['Over 25.5 Points', 'Under 25.5 Points', 'Lakers -3.5', 'Over 220.5 Total'],
            'NFL': ['Over 250 Passing Yards', 'Under 250 Passing Yards', 'Patriots -7', 'Over 45.5 Total'],
            'MLB': ['Over 8.5 Total', 'Under 8.5 Total', 'Yankees -1.5', 'Over 0.5 Home Runs']
        };
        const leagueBets = bets[league] || ['Over/Under', 'Spread', 'Moneyline'];
        return leagueBets[Math.floor(Math.random() * leagueBets.length)];
    };
    
    // Clear all filters
    const clearAllFilters = () => {
        setBetType('straight');
        setSport('all');
        setLeague('all');
        setMarket('all');
        setBook('all');
        setTeam('all');
        setStartTime('next_24h');
        setOddsRange({ min: '', max: '' });
        setBookCount({ min: 1, max: 20 });
        setBoostPercent('0');
        setSharpBook('all');
        setFuture(false);
        setLive(false);
        setSearchQuery('');
        setHomeTeams([]);
        setAwayTeams([]);
        setConference('all');
        setDivision('all');
        setPlayers([]);
        setPosition('all');
        setExperience('all');
        setSpreadRange({ min: '', max: '' });
        setTotalRange({ min: '', max: '' });
        setEvRange({ min: '', max: '' });
        setKellyCriterion('0.25');
        setSelectedSportsbooks([]);
    };
    
    // Get current filtered options
    const currentOptions = getFilteredOptions(sport);

    // Derive which result sections are present (used for section filter tabs)
    const presentSections = useMemo(
        () => [...new Set(results.map(r => r._resultSection).filter(Boolean))],
        [results]
    );

    // Derive unique player names from player prop results (used for player filter chips)
    const uniquePlayers = useMemo(() => {
        const propResults = results.filter(r => r._resultSection === 'player_props');
        return [...new Set(propResults.map(r => r.raw?.selection || '').filter(Boolean))].sort();
    }, [results]);

    // Apply section + player filters to produce the list that gets paginated
    const filteredResults = useMemo(() => {
        let filtered = results;
        if (activeSection !== 'all') {
            filtered = filtered.filter(r => r._resultSection === activeSection);
        }
        if (activePlayerFilter) {
            filtered = filtered.filter(r => {
                const sel = r.raw?.selection || '';
                return sel === activePlayerFilter || r.bet?.startsWith(activePlayerFilter);
            });
        }
        return filtered;
    }, [results, activeSection, activePlayerFilter]);

    // Handle bet selection/deselection
    const toggleBetSelection = (result) => {
        setSelectedBets(prev => {
            const isSelected = prev.some(bet => bet.id === result.id);
            if (isSelected) {
                return prev.filter(bet => bet.id !== result.id);
            } else {
                return [...prev, result];
            }
        });
    };
    
    // Send selected bets to SGP builder
    const sendToSGPBuilder = () => {
        if (selectedBets.length === 0) return;
        
        // Store selected bets in session storage for SGP builder to pick up
        if (Platform.OS === 'web') {
            try {
                window.sessionStorage.setItem('sgp:selectedBets', JSON.stringify(selectedBets));
            } catch (e) {
                console.error('Failed to save selected bets:', e);
            }
        }
        
        // Navigate to SGP builder
        navigateTo('SgpFlow');
    };

    // True when the backend sent a requested line and at least one result has line data
    const hasRequestedLineForRender = results.some(r => r.isRequestedLine || r._isClosestLine || r.lineDistance != null);


    const renderResultRow = (result, index) => {
        const isSelected = selectedBets.some(bet => bet.id === result.id);
        return (
            <HoverableTableRow
                key={result.id}
                style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}
                hoveredStyle={styles.tableRowHovered}
            >
                <Pressable
                    style={[styles.tableCell, styles.selectCell]}
                    onPress={() => toggleBetSelection(result)}
                >
                    {({ hovered }) => (
                        <View style={[styles.selectCheckbox, isSelected && styles.selectCheckboxActive, hovered && !isSelected && styles.selectCheckboxHovered]}>
                            {isSelected && <Ionicons name="checkmark" size={12} color="#fff" />}
                        </View>
                    )}
                </Pressable>
                <Text style={styles.tableCell}>{result.league}</Text>
                <Text style={styles.tableCell}>{result.date}</Text>
                <Text style={styles.tableCell}>{result.time}</Text>
                <Text style={styles.tableCell}>{result.game}</Text>
                <Text style={styles.tableCell}>{result.market}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, flexWrap: 'wrap', gap: 4 }}>
                    <Text style={styles.tableCell}>{result.bet}</Text>
                    {hasRequestedLineForRender && result.isRequestedLine && result.directionMatch && (
                        <View style={[styles.mainAltBadge, { backgroundColor: '#10b981' }]}>
                            <Text style={styles.mainAltBadgeText}>REQUESTED</Text>
                        </View>
                    )}
                    {hasRequestedLineForRender && result._isClosestLine && result.directionMatch && (
                        <View style={[styles.mainAltBadge, { backgroundColor: '#f59e0b' }]}>
                            <Text style={styles.mainAltBadgeText}>CLOSEST</Text>
                        </View>
                    )}
                    {(!hasRequestedLineForRender) && result.isMain !== undefined && (
                        <View style={[styles.mainAltBadge, result.isMain ? styles.mainBadge : styles.altBadge]}>
                            <Text style={styles.mainAltBadgeText}>{result.isMain ? 'MAIN' : 'ALT'}</Text>
                        </View>
                    )}
                    {result._isPlayerProp && (
                        <View style={[styles.mainAltBadge, { backgroundColor: '#8b5cf6' }]}>
                            <Text style={styles.mainAltBadgeText}>PROP</Text>
                        </View>
                    )}
                    {result.isInjured && result.injuryStatus && (
                        <View style={[styles.injuryBadgeSmall, result.injuryStatus === 'out' && styles.injuryBadgeSmallOut, result.injuryStatus === 'doubtful' && styles.injuryBadgeSmallDoubtful]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                <Ionicons name="medical" size={8} color="#fff" />
                                <Text style={styles.injuryBadgeSmallText}>{result.injuryStatus.toUpperCase()}</Text>
                            </View>
                        </View>
                    )}
                </View>
                <Text style={styles.tableCell} numberOfLines={1}>{result.book}</Text>
                <Text style={styles.tableCell}>{result.odds}</Text>
                <View style={{ flex: 1, alignItems: 'flex-start', justifyContent: 'center', paddingHorizontal: 4 }}>
                    <Text style={[styles.tableCell, { paddingHorizontal: 0 }, result.booksCalculated >= 10 && styles.goodBookCount, result.booksCalculated < 5 && styles.lowBookCount]}>
                        {result.booksCalculated}
                    </Text>
                    {(() => {
                        const allBks = (result.allBooks || []).map(b => (b || '').toLowerCase().replace(/[^a-z0-9]/g, ''));
                        const t1Labels = [
                            { key: 'draftkings', label: 'DK' },
                            { key: 'fanduel', label: 'FD' },
                            { key: 'caesars', label: 'CZR' },
                            { key: 'betmgm', label: 'MGM' },
                        ].filter(({ key }) => allBks.some(b => b.startsWith(key)));
                        if (t1Labels.length === 0) return null;
                        return (
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 2, marginTop: 2 }}>
                                {t1Labels.map(({ label }) => (
                                    <View key={label} style={{ backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 3, paddingHorizontal: 3, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(16,185,129,0.35)' }}>
                                        <Text style={{ color: '#10b981', fontSize: 8, fontWeight: '700' }}>{label}</Text>
                                    </View>
                                ))}
                            </View>
                        );
                    })()}
                </View>
                <Text style={styles.tableCell}>{result.fairValue}</Text>
                <Text style={[styles.tableCell, parseFloat(result.evPercent) >= 0 ? styles.positiveValue : styles.negativeValue]}>
                    {result.evPercent}%
                </Text>
                <Text style={styles.tableCell}>{result.kelly}%</Text>
            </HoverableTableRow>
        );
    };
    
  return (
    <UnifiedSearchScreenView
      ALL_SPORTSBOOKS={ALL_SPORTSBOOKS}
      BOOST_OPTIONS={BOOST_OPTIONS}
      activePlayerFilter={activePlayerFilter}
      activeSection={activeSection}
      book={book}
      boostPercent={boostPercent}
      cacheStatus={cacheStatus}
      clearAllFilters={clearAllFilters}
      clearSportsbookSelections={clearSportsbookSelections}
      currentOptions={currentOptions}
      currentPage={currentPage}
      currentQueryId={currentQueryId}
      evRange={evRange}
      executeSearch={executeSearch}
      filteredResults={filteredResults}
      fixtureInfo={fixtureInfo}
      future={future}
      injuryAlert={injuryAlert}
      isAdmin={isAdmin}
      kellyCriterion={kellyCriterion}
      league={league}
      live={live}
      loading={loading}
      market={market}
      presentSections={presentSections}
      queryAssessment={queryAssessment}
      queryResolution={queryResolution}
      renderResultRow={renderResultRow}
      resolvedSearchText={resolvedSearchText}
      resultType={resultType}
      results={results}
      searchInputFocused={searchInputFocused}
      searchInputRef={searchInputRef}
      searchQuery={searchQuery}
      selectPopularSportsbooks={selectPopularSportsbooks}
      selectedBets={selectedBets}
      selectedSportsbooks={selectedSportsbooks}
      sendToSGPBuilder={sendToSGPBuilder}
      setActivePlayerFilter={setActivePlayerFilter}
      setActiveSection={setActiveSection}
      setBook={setBook}
      setBoostPercent={setBoostPercent}
      setCurrentPage={setCurrentPage}
      setEvRange={setEvRange}
      setFuture={setFuture}
      setKellyCriterion={setKellyCriterion}
      setLeague={setLeague}
      setLive={setLive}
      setMarket={setMarket}
      setResolvedSearchText={setResolvedSearchText}
      setSearchInputFocused={setSearchInputFocused}
      setSearchQuery={setSearchQuery}
      setShowAdvanced={setShowAdvanced}
      setShowOriginalHint={setShowOriginalHint}
      setShowSearchSuggestions={setShowSearchSuggestions}
      setShowSportsbooksDropdown={setShowSportsbooksDropdown}
      setSport={setSport}
      setStartTime={setStartTime}
      showAdvanced={showAdvanced}
      showOriginalHint={showOriginalHint}
      showSearchSuggestions={showSearchSuggestions}
      showSportsbooksDropdown={showSportsbooksDropdown}
      sport={sport}
      startTime={startTime}
      timeRanges={timeRanges}
      toggleSportsbook={toggleSportsbook}
      totalHeaderHeight={totalHeaderHeight}
      totalResults={totalResults}
      totalSportsbooks={totalSportsbooks}
      uniquePlayers={uniquePlayers}
      wipeAnim={wipeAnim}
      wipeComplete={wipeComplete}
    />
  );
};


export default UnifiedSearchScreen;
